import { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link, useLocation } from 'react-router-dom';
import { COLORS } from '../../config/colors';
import { LOGOS } from '../../config/logos';
import MaintenanceModal from '../common/MaintenanceModal';

export default function NavbarInstitut() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    AOS.init({ duration: 800, once: true, offset: 100, easing: 'ease-in-out' });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMaintenanceClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMaintenanceModalOpen(true);
  };

  const menuItems = [
    { label: 'Accueil', path: '/institut' },
    {
      label: 'Établissement',
      submenu: [
        { label: 'Instances', path: '/institut/instances', isMaintenance: false },
        { label: 'Équipe', path: '/institut/equipe', isMaintenance: false },
        { label: 'Mot du Recteur', path: '/institut/mot-directeur', isMaintenance: false },
      ],
    },
    {
      label: 'Inscription',
      submenu: [
        { label: 'Frais de Scolarité', path: '/institut/frais', isMaintenance: false },
        { label: 'Nos Programmes', path: '/institut/fourniture', isMaintenance: false },
      ],
    },
    {
      label: 'Parcours Étudiant',
      submenu: [
        { label: 'Licence', path: '/institut/licence', isMaintenance: false },
        { label: 'Master', path: '/institut/master', isMaintenance: false },
        // { label: 'Doctorat', path: '/institut/doctorat', isMaintenance: false },
        { label: 'Orientation', path: '/institut/orientation', isMaintenance: false },
      ],
    },
    {
      label: 'Infos Pratiques',
      submenu: [
        { label: 'Calendrier', path: '/institut/calendrier' , isMaintenance: false },
        { label: 'Association Sportive', path: '/institut/association-sportive', isMaintenance: false },
        { label: 'Activités Extra-universitaires', path: '/institut/activites-extra-universitaires', isMaintenance: false },
        { label: 'Centre Entrepreneuriat', path: '/institut/entrepreneuriat',isMaintenance: false },
      ],
    },
    {
      label: 'Actualités',
      submenu: [
        { label: 'Événements', path: '/institut/evenements', isMaintenance: false },
        { label: 'Projets', path: '/institut/projets', isMaintenance: false },
        { label: 'Galerie', path: '/institut/galerie', isMaintenance: false },
      ],
    },
    // {
    //   label: 'Outils',
    //   submenu: [
    //     { label: 'Esidoc', path: '/institut/esidoc' },
    //     { label: 'Pronote', path: '/institut/pronote' },
    //   ],
    // },
    { label: 'Contact', path: '/institut/contact', isMaintenance: false },
  ];

  const handleMouseEnter = (label: string) => {
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/institut" className="flex items-center space-x-3 group">
              <img 
                src={LOGOS.institut} 
                alt="Institut" 
                className="h-16 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden xl:flex items-center gap-6">
              {menuItems.map((item) =>
                item.submenu ? (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button className="text-sm font-medium text-gray-800 hover:opacity-100 opacity-90 transition-all duration-300 flex items-center gap-1 whitespace-nowrap cursor-pointer hover:scale-105">
                      {item.label}
                      <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${openDropdown === item.label ? 'rotate-180' : ''}`}></i>
                    </button>
                    <div 
                      className={`absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl py-2 min-w-[220px] transition-all duration-300 ${
                        openDropdown === item.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}
                      onMouseEnter={() => handleMouseEnter(item.label)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.path}
                          onClick={subItem.isMaintenance ? handleMaintenanceClick : undefined}
                          className="flex items-center justify-between px-5 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-transparent transition-all duration-300 whitespace-nowrap hover:translate-x-1"
                          style={{ color: location.pathname === subItem.path ? COLORS.primary : undefined }}
                        >
                          <span>{subItem.label}</span>
                          {subItem.isMaintenance && (
                            <span 
                              className="ml-3 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full text-white"
                              style={{ backgroundColor: COLORS.primary + 'CC' }}
                            >
                              Travaux
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-sm font-medium transition-all duration-300 hover:opacity-100 hover:scale-105 whitespace-nowrap ${
                      location.pathname === item.path ? 'opacity-100 font-semibold' : 'opacity-90'
                    }`}
                    style={{ color: location.pathname === item.path ? COLORS.primary : '#1f2937' }}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden p-2 rounded-lg transition-all duration-300 cursor-pointer hover:scale-110"
              style={{ color: COLORS.primary }}
            >
              <i className={`text-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`xl:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? 'max-h-[800px] opacity-100 mt-4 pb-4 border-t border-gray-200' : 'max-h-0 opacity-0'
          }`}>
            <div className="flex flex-col gap-3 mt-4">
              {menuItems.map((item) =>
                item.submenu ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                      className="w-full text-left text-base font-medium text-gray-800 py-2 flex items-center justify-between cursor-pointer transition-all duration-300 hover:translate-x-1"
                    >
                      {item.label}
                      <i className={`ri-arrow-${openDropdown === item.label ? 'up' : 'down'}-s-line text-lg transition-transform duration-300`}></i>
                    </button>
                    <div className={`ml-4 flex flex-col gap-2 overflow-hidden transition-all duration-300 ${
                      openDropdown === item.label ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
                    }`}>
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.path}
                          onClick={(e) => {
                            if (subItem.isMaintenance) {
                              handleMaintenanceClick(e);
                            } else {
                              setIsMobileMenuOpen(false);
                            }
                          }}
                          className="flex items-center justify-between text-sm text-gray-700 py-1.5 transition-all duration-300 hover:translate-x-1"
                          style={{ color: location.pathname === subItem.path ? COLORS.primary : undefined }}
                        >
                          <span>{subItem.label}</span>
                          {subItem.isMaintenance && (
                            <span 
                              className="px-2 py-0.5 text-[8px] font-bold uppercase rounded-full text-white"
                              style={{ backgroundColor: COLORS.primary + 'CC' }}
                            >
                              Travaux
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-base font-medium text-gray-800 py-2 transition-all duration-300 hover:translate-x-1"
                    style={{ color: location.pathname === item.path ? COLORS.primary : undefined }}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      </nav>

      <MaintenanceModal 
        isOpen={isMaintenanceModalOpen} 
        onClose={() => setIsMaintenanceModalOpen(false)} 
      />
    </>
  );
}
